import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Dataset } from '@models/mood/dataset';
import { DatasetListingService } from 'src/app/dataset-listing/dataset-listing.service';
import { AdminService } from '../../admin/admin.service';
import { SnackbarService } from 'src/app/core/snackbar.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ColorScaleForm, ColorScaleFormComponent } from '../color-scale-form/color-scale-form.component';
import { ExternalReferenceForm, ExternalReferenceFormComponent } from '../reference-form/reference-form.component';
import { ExternalReference } from '@models/mood/dataset-metainfo';
import { GroupedDatasetConfigurationForm, GroupedDatasetConfigurationFormComponent } from '../grouped-dataset-metadata-form/grouped-dataset-metadata-form.component';
import { GroupedFileMetadata, GroupedMetadata } from '@models/mood/metadata';
import { GroupedFileFormComponent } from '../grouped-file-form/grouped-file-form.component';
import { COVARIATE_DATASET_TYPE_ENUM } from '@models/mood/covariates/dataset';
import { Category } from '@models/mood/covariates/category';
import { GroupedTreeNode } from 'src/app/core/datasets/tree.interface';
import { TreeGeneratorService } from 'src/app/core/datasets/tree-generator.service';
import { DatasetProviderEntry, ProviderForm, ProviderSelectionFormComponent } from '../provider-selection-form/provider-selection-form.component';


interface DatasetConfigForm {
  name: FormControl<string | null>;
  description: FormControl<string | null>;

  validated: FormControl<boolean | null>;
  color_scale?: FormGroup<ColorScaleForm>;

  group_order: FormControl<number | null>;

  category_id: FormControl<string | null>;

  metadata: FormGroup<GroupedDatasetConfigurationForm>;
  external_references: FormArray<FormGroup<ExternalReferenceForm>>;
  providers: FormArray<FormGroup<ProviderForm>>;
}

@Component({
  selector: 'mood-admin-covariate-form',
  templateUrl: './covariate-form.component.html',
  styleUrls: [
    'covariate-form.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CovariateFormComponent implements OnInit {
  private _currentCovariateId!: string;

  @Input()
  set covariateId(value: string) {
    this._currentCovariateId = value;

    this._loadAndPatchExistingData();
  }

  get covariateId(): string {
    return this._currentCovariateId;
  }

  get isTimeseriesDataset() {
    return this.currentDataset?.type === COVARIATE_DATASET_TYPE_ENUM.TIMESERIES;
  }

  get isGroupedDataset() {
    return this.currentDataset?.type === COVARIATE_DATASET_TYPE_ENUM.GROUPED || this.currentDataset?.type === COVARIATE_DATASET_TYPE_ENUM.FOURIER;
  }

  datasetForm: FormGroup;

  currentDataset?: Dataset;


  private _categoryList!: Category[];
  categoryTreeList!: GroupedTreeNode[];

  @ViewChild(ColorScaleFormComponent) colorScaleForm!: ColorScaleFormComponent;

  constructor(private _adminService: AdminService,
    private _snackbarService: SnackbarService,
    private _dataListingService: DatasetListingService,
    private _router: Router,
    private _treeService: TreeGeneratorService
  ) {
    this.datasetForm = new FormGroup<DatasetConfigForm>({
      name: new FormControl<string | null>(null, [Validators.required]),
      description: new FormControl<string | null>(null),
      validated: new FormControl<boolean | null>(null),
      group_order: new FormControl<number | null>(null),
      category_id: new FormControl<string | null>(null),
      metadata: GroupedDatasetConfigurationFormComponent.createMetadataFormGroup(),
      external_references: new FormArray<FormGroup>([]),
      providers: new FormArray<FormGroup>([])
    });
  }

  addExternalReference(existingReference?: ExternalReference) {
    this.externalReferencesArray.push(ExternalReferenceFormComponent.createExternalReferenceFormGroup(existingReference));
    this.externalReferencesArray.markAsDirty();
  }

  removeReferenceEntry(index: number) {
    this.externalReferencesArray.removeAt(index);
    this.externalReferencesArray.markAsDirty();
  }

  addProvider(existingProvider?: DatasetProviderEntry) {
    this.providersArray.push(ProviderSelectionFormComponent.createProviderFormGroup(existingProvider));
    this.providersArray.markAsDirty();
  }

  removeProviderEntry(index: number) {
    this.providersArray.removeAt(index);
    this.providersArray.markAsDirty();
  }

  addFile(existingGroupFile: GroupedFileMetadata) {
    this.filesArray.push(GroupedFileFormComponent.createMetadataFormGroup(existingGroupFile));
  }

  customColorScaleControl = new FormControl<boolean>(false);

  get hasCustomColorScale() {
    return this.customColorScaleControl.value;
  }

  get externalReferencesArray() {
    return this.datasetForm.get('external_references') as FormArray;
  }

  get providersArray() {
    return this.datasetForm.get('providers') as FormArray;
  }

  get saveButtonDisabled() {
    return this.datasetForm.invalid || (this.datasetForm.pristine && this.customColorScaleControl.pristine);
  }

  async ngOnInit() {
    this.customColorScaleControl.valueChanges.subscribe(scaleEnabled => {
      if (scaleEnabled) {
        this.datasetForm.addControl('color_scale', ColorScaleFormComponent.createColorScaleFormGroup(this.currentDataset?.color_scale_id, this.currentDataset?.color_scale_invert));
      } else {
        this.datasetForm.get('color_scale')?.reset();
        this.datasetForm.removeControl('color_scale');
      }
    });

    this._adminService.categoryUpdated.subscribe(async _ => {
      await this._generateTree();
    });

    await this._generateTree();
  }

  public get currentCategoryId() {
    return this.datasetForm.get('category_id')!.value!;
  }

  public updateCategory(value: string) {
    this.datasetForm.patchValue({ category_id: value });
    this.datasetForm.markAsDirty();
  }

  private async _generateTree() {
    this._categoryList = await this._dataListingService.getDatasetCategories();
    this.categoryTreeList = this._treeService.createCategoryTree(this._categoryList);

    // TODO:
    // this._changeRef.markForCheck();
  }

  private _clearExistingForm() {
    this.datasetForm.reset();
    this.customColorScaleControl.reset();

    while (this.externalReferencesArray.length !== 0) {
      this.externalReferencesArray.removeAt(0)
    }

    if (this.filesArray) {
      while (this.filesArray.length !== 0) {
        this.filesArray.removeAt(0);
      }
    }

    while (this.providersArray.length !== 0){
      this.providersArray.removeAt(0);
    }
  }

  private async _loadAndPatchExistingData() {
    this._clearExistingForm();

    this.currentDataset = await this._dataListingService.findDatasetById(this.covariateId);

    if (!this.currentDataset) {
      this._snackbarService.openErrorMessage("Covariate dataset not found")

      this._router.navigate(['/', 'admin']);
    }

    if (this.currentDataset?.color_scale_id) {
      this.customColorScaleControl.patchValue(true);
    }

    this.currentDataset?.external_references?.forEach(reference => {
      this.addExternalReference(reference);
    });

    this.currentDataset?.providers?.forEach(provider => {
      this.addProvider(provider);
    });

    (this.currentDataset?.metadata as GroupedMetadata).files?.forEach(outputFile => {
      this.addFile(outputFile);
    });

    this.datasetForm.patchValue(this.currentDataset!);
    this.datasetForm.markAsPristine();
  }

  get groupedMetadataFormGroup() {
    return this.datasetForm.get('metadata') as FormGroup<GroupedDatasetConfigurationForm>;
  }

  async saveForm() {
    // TODO: Handle failure

    const updatedData = await this._adminService.updateDataset(this.covariateId, this.datasetForm.getRawValue());
    await this._dataListingService.updateDatasetById(this.covariateId, updatedData)

    this._adminService.datasetCategoryUpdated.emit();

    this.datasetForm.markAsPristine();
    this.customColorScaleControl.markAsPristine();
    // this.customRasterLayerConfiguration.markAsPristine();

    this._snackbarService.openSuccessMessage("Dataset configuration updated.")
  }


  get filesArray() {
    return this.groupedMetadataFormGroup?.get('files') as FormArray;
  }
}
